import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import PrivateLayout from '@/components/layout/auth/PrivateLayout';
import Loader from '@/components/ui/loader';
import { useI18n } from '@/hooks/useI18n';
import { useAuth } from '@/hooks/useAuth';
import { useMoneyCopy } from '@/lib/i18n/money-copy';
import { useStripeReturnFeedback } from '@/hooks/useStripeReturnFeedback';
import { useCreateItineraryPaymentLink } from '@/lib/builder/hooks/useItineraryPayment';
import { PaymentPlanEditorModal } from '@/lib/builder/components/payment-plan/PaymentPlanEditorModal';
import { formatPlanAmount } from '@/lib/builder/utils/payment-format';
import { PaymentsFilters } from '@/lib/payments/components/PaymentsFilters';
import { PaymentsKpiRow } from '@/lib/payments/components/PaymentsKpiRow';
import { PlatformFeeDueBanner } from '@/lib/payments/components/PlatformFeeDueBanner';
import { DueInstallmentsCard } from '@/lib/payments/components/DueInstallmentsCard';
import { RecentPaymentsCard } from '@/lib/payments/components/RecentPaymentsCard';
import { PaymentsItinerariesCard } from '@/lib/payments/components/PaymentsItinerariesCard';
import { PaymentsPagination } from '@/lib/payments/components/PaymentsPagination';
import {
    useMarkInstallmentPaidFromDashboard,
    useSendInstallmentReminder,
    usePaymentsDashboardError,
    usePaymentsDashboardQuery,
    PAYMENTS_FILTER_OPTIONS_QUERY_KEY,
} from '@/lib/payments/hooks/usePaymentsDashboard';
import { usePaymentsFacets } from '@/lib/payments/hooks/usePaymentsFacets';
import {
    DEFAULT_DUE_WINDOW,
    DEFAULT_PAGE_SIZE,
    DUE_WINDOW_OPTIONS,
} from '@/lib/payments/models/payments-dashboard.model';
import { PaymentsFacetSelection } from '@/lib/payments/models/payments-filters.model';

/** Quante rate incassate mostrare nello storico recente. */
const RECENT_LIMIT = 10;

/**
 * Dashboard pagamenti.
 *
 * Sola lettura piu' le azioni che esistono gia' altrove, tutte riusate senza copiarle:
 * registrare un incasso (mark_installment_paid, la stessa della card nel builder), pagare
 * (create-itinerary-payment-link, la stessa della modale del viaggiatore) e modificare il
 * piano (PaymentPlanEditorModal, la stessa modale dello step Checkout).
 *
 * Si apre sempre il piano **intero**, anche partendo da una singola rata: gli importi
 * devono quadrare col totale dell'itinerario, e una riga modificata da sola sfonderebbe
 * la quadratura senza mostrare dove.
 *
 * La visibilita' non e' decisa in questa pagina: la RPC filtra con can_view_itinerary(),
 * la stessa funzione delle policy RLS. Il ruolo qui serve solo a scegliere quali colonne
 * e quali pulsanti mostrare.
 */
const PaymentsPage = () => {
    const { t } = useI18n();
    const money = useMoneyCopy();
    const { userRole, userData, loading: authLoading } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();

    const isTraveler = userRole === 'traveler';
    // Chi gestisce le rate — modifica del piano, solleciti: gli stessi che passano
    // can_manage_itinerary().
    const canManage =
        userRole === 'admin' ||
        userRole === 'agency' ||
        userRole === 'travel_designer' ||
        userRole === 'travel_advisor_member';
    // Registrare un incasso a mano e' tornato a loro il 02/09/2026, dopo la parentesi
    // admin-only del 26/08. Resta vero che e' una scrittura contabile che nessuno verifica
    // contro Stripe, ma ora non e' piu' gratuita: apre una commissione a debito con sopra
    // il nome di chi l'ha dichiarata, e gli admin ne ricevono notifica. Il presidio si e'
    // spostato dal permesso alla tracciabilita'. Il guard vero sta in
    // `mark_installment_paid` (can_manage_itinerary): qui si nasconde solo cio' che
    // verrebbe comunque rifiutato.
    const canMarkPaid = canManage;
    // La colonna "collaboratore" e' per chi ne vede piu' di uno: admin e agenzia. Advisor
    // e membri di agenzia vedono solo le proprie rate, quindi ripeterebbe il loro nome su
    // ogni riga.
    const showDesigner = userRole === 'admin' || userRole === 'agency';

    // Finestra, filtri e pagina stanno nella URL: la pagina resta condivisibile e il
    // ritorno dal builder non azzera il lavoro di filtratura.
    // Il parametro va controllato come stringa prima di convertirlo: Number(null) e' 0, e
    // 0 e' l'opzione "tutto il periodo": senza il null-check la pagina si aprirebbe senza
    // filtro invece che sui 30 giorni di default.
    const windowParam = searchParams.get('window');
    const dueWindowDays =
        windowParam !== null && DUE_WINDOW_OPTIONS.includes(Number(windowParam) as never)
            ? Number(windowParam)
            : DEFAULT_DUE_WINDOW;

    const selection: PaymentsFacetSelection = useMemo(
        () => ({
            itineraryId: searchParams.get('itinerary'),
            clientKey: searchParams.get('client'),
            designerId: searchParams.get('designer'),
        }),
        [searchParams]
    );

    /**
     * Interruttore "escludi proposte non accettate", acceso di default: la pagina serve a
     * lavorare gli incassi, e le scadenze di una bozza o di una proposta che il cliente
     * non ha accettato non sono crediti — mescolate alle rate vere gonfiano residuo,
     * scaduto e coda dei solleciti.
     *
     * Il parametro nella URL e' l'eccezione, non lo stato: assente = interruttore acceso,
     * `?showProposals=1` = spento. Cosi' la URL nuda mostra la pagina come la si vuole
     * normalmente, e sono i link con il filtro allentato a portarsi dietro il perche'.
     * Sta comunque nella URL come gli altri filtri: e' una lettura diversa della pagina, e
     * chi la manda a un collega deve mandare anche quella.
     */
    const excludeUnaccepted = searchParams.get('showProposals') !== '1';

    const duePage = Math.max(1, Number(searchParams.get('duePage')) || 1);
    const itinerariesPage = Math.max(1, Number(searchParams.get('tripsPage')) || 1);

    const updateParams = (changes: Record<string, string | null>) => {
        const next = new URLSearchParams(searchParams);
        for (const [key, value] of Object.entries(changes)) {
            if (value === null) next.delete(key);
            else next.set(key, value);
        }
        setSearchParams(next, { replace: true });
    };

    /**
     * Cambiare un filtro riporta entrambe le liste a pagina 1: restare a pagina 4 di un
     * insieme che si e' appena ristretto a due pagine mostrerebbe una tabella vuota, e
     * sembrerebbe che il filtro non abbia trovato nulla.
     */
    const applySelection = (next: PaymentsFacetSelection) =>
        updateParams({
            itinerary: next.itineraryId,
            client: next.clientKey,
            designer: next.designerId,
            duePage: null,
            tripsPage: null,
        });

    const facets = usePaymentsFacets(selection, applySelection);

    const params = useMemo(
        () => ({
            dueWindowDays,
            excludeUnaccepted,
            designerId: selection.designerId,
            itineraryId: selection.itineraryId,
            clientKey: selection.clientKey,
            recentLimit: RECENT_LIMIT,
            duePage,
            itinerariesPage,
            pageSize: DEFAULT_PAGE_SIZE,
        }),
        [dueWindowDays, excludeUnaccepted, selection, duePage, itinerariesPage]
    );

    const { data, isLoading, isFetching, error, refetch } = usePaymentsDashboardQuery(params);
    usePaymentsDashboardError(error);

    const queryClient = useQueryClient();

    /**
     * Il refresh manuale butta via anche le opzioni dei filtri, non solo i dati.
     *
     * Le opzioni restano in cache dieci minuti perche' cambiano di rado, ma un piano creato
     * da un collega in questo momento non le invalida: e' proprio il caso in cui si preme
     * refresh, e se il pulsante non le toccasse il viaggio nuovo resterebbe fuori dalle
     * tendine senza alcun modo di farlo comparire.
     */
    const handleRefresh = () => {
        queryClient.invalidateQueries({ queryKey: [PAYMENTS_FILTER_OPTIONS_QUERY_KEY] });
        refetch();
    };

    // Rientro da Stripe Checkout: e' qui che tornano sia il saldo unico sia la singola
    // rata pagata dalla piattaforma. Il refetch serve perche' nel frattempo il webhook
    // puo' aver gia' marcato la rata.
    useStripeReturnFeedback({ onSuccess: refetch });

    const markPaid = useMarkInstallmentPaidFromDashboard();
    const createPaymentLink = useCreateItineraryPaymentLink();
    const sendReminder = useSendInstallmentReminder();

    /**
     * Rate sollecitate da quando la pagina e' aperta. Serve solo a disattivare il pulsante:
     * il vincolo vero e' il throttle di un'ora nella RPC, che vale anche fra sessioni e
     * fra utenti diversi della stessa agenzia.
     */
    const [remindedIds, setRemindedIds] = useState<Set<string>>(new Set());

    /**
     * Rata per cui e' in corso l'apertura del checkout, o null.
     *
     * Resta valorizzata anche a mutation riuscita: `onSuccess` fa `window.location.href`,
     * e la navigazione non e' istantanea. Guardando solo `isPending` il pulsante tornerebbe
     * "Paga" per un istante prima che la pagina cambi, proprio mentre l'utente aspetta.
     */
    const payingInstallmentId =
        createPaymentLink.isPending || createPaymentLink.isSuccess
            ? createPaymentLink.variables?.installmentId ?? null
            : null;

    /**
     * Itinerario di cui e' aperto l'editor del piano. Una sola istanza della modale per
     * tutta la pagina: e' la stessa operazione da qualunque riga la si apra, e montarne
     * una per riga vorrebbe dire una query del piano per ogni viaggio a schermo.
     *
     * Il totale non serve passarlo: la modale usa `plan.itinerary_total`, che la RPC legge
     * dal database. Qui non c'e' un itinerario aperto in memoria da cui prenderlo.
     */
    const [planToEdit, setPlanToEdit] = useState<{
        itineraryId: string;
        installmentId?: string;
    } | null>(null);

    // Primo caricamento: senza dati non c'e' nulla da impaginare. I refetch successivi
    // restano silenziosi (placeholderData tiene a schermo il risultato precedente).
    if (authLoading || (isLoading && !data)) {
        return (
            <PrivateLayout pageTitle={t('payments.title')}>
                <div className="flex justify-center py-16">
                    <Loader />
                </div>
            </PrivateLayout>
        );
    }

    return (
        <PrivateLayout
            pageTitle={t('payments.title')}
            pageDescription={t(`${money}.pageSubtitle`)}
        >
            <div className="flex flex-col gap-4">
                <PaymentsFilters
                    dueWindowDays={dueWindowDays}
                    onDueWindowChange={(days) =>
                        updateParams({ window: String(days), duePage: null, tripsPage: null })
                    }
                    selection={selection}
                    onSelectionChange={applySelection}
                    itineraryOptions={facets.itineraryOptions}
                    clientOptions={isTraveler ? undefined : facets.clientOptions}
                    designerOptions={showDesigner ? facets.designerOptions : undefined}
                    excludeUnaccepted={excludeUnaccepted}
                    /* Come gli altri filtri riporta a pagina 1: cambiando l'insieme,
                       restare a pagina 4 lascerebbe due tabelle vuote che sembrano
                       "nessun risultato". */
                    onExcludeUnacceptedChange={(value) =>
                        updateParams({
                            showProposals: value ? null : '1',
                            duePage: null,
                            tripsPage: null,
                        })
                    }
                    facetQuery={facets.query}
                    onFacetQueryChange={facets.setQuery}
                    facetsTruncated={facets.truncated}
                    facetsSearching={facets.isSearching}
                    facetsTotalCount={facets.totalCount}
                    isRefreshing={isFetching}
                    onRefresh={handleRefresh}
                />

                {data && (
                    <PlatformFeeDueBanner
                        amount={data.kpis.platform_fee_due_amount}
                        count={data.kpis.platform_fee_due_count}
                    />
                )}

                {data && (
                    <>
                        {/* Colonna sinistra: i KPI sopra, il carosello dei viaggi sotto.
                            Colonna destra: gli ultimi incassi, alti quanto le due righe
                            insieme — la lista si allunga invece di lasciare un vuoto. */}
                        <div className="grid gap-4 grid-cols-1 xl:grid-cols-10 items-stretch">
                            <div className="xl:col-span-7 min-w-0 flex flex-col gap-4">
                                <PaymentsKpiRow
                                    kpis={data.kpis}
                                    dueWindowDays={data.due_window_days}
                                />

                                <PaymentsItinerariesCard
                                    rows={data.itineraries}
                                    people={data.people}
                                    isLoading={isFetching}
                                    canManage={canManage}
                                    showDesigner={showDesigner}
                                    excludeUnaccepted={excludeUnaccepted}
                                    onEditPlan={(itineraryId) => setPlanToEdit({ itineraryId })}
                                    footer={
                                        <PaymentsPagination
                                            page={data.itineraries_page}
                                            isLoading={isFetching}
                                            onPageChange={(page) =>
                                                updateParams({ tripsPage: String(page) })
                                            }
                                        />
                                    }
                                />
                            </div>

                            <div className="xl:col-span-3 min-w-0 h-full">
                                <RecentPaymentsCard
                                    rows={data.recent_paid}
                                    people={data.people}
                                    isLoading={isFetching}
                                />
                            </div>
                        </div>

                        <DueInstallmentsCard
                            rows={data.due_installments}
                            people={data.people}
                            dueWindowDays={data.due_window_days}
                            isLoading={isFetching}
                            canManage={canManage}
                            canMarkPaid={canMarkPaid}
                            canPay={isTraveler}
                            travelerView={isTraveler}
                            isMarkingPaid={markPaid.isPending}
                            payingInstallmentId={payingInstallmentId}
                            isSendingReminder={sendReminder.isPending}
                            remindedIds={remindedIds}
                            onSendReminder={({ installmentId }) =>
                                sendReminder.mutate(
                                    { installmentId },
                                    {
                                        onSuccess: () =>
                                            setRemindedIds((prev) =>
                                                new Set(prev).add(installmentId)
                                            ),
                                    }
                                )
                            }
                            onMarkPaid={(payload) => markPaid.mutate(payload)}
                            onPay={({ itineraryId, installmentId }) =>
                                createPaymentLink.mutate({
                                    itineraryId,
                                    installmentId,
                                    customerEmail: userData?.email ?? null,
                                })
                            }
                            onEditPlan={(itineraryId, installmentId) =>
                                setPlanToEdit({ itineraryId, installmentId })
                            }
                            showDesigner={showDesigner}
                            footer={
                                <PaymentsPagination
                                    page={data.due_installments_page}
                                    isLoading={isFetching}
                                    onPageChange={(page) =>
                                        updateParams({ duePage: String(page) })
                                    }
                                />
                            }
                        />
                    </>
                )}
            </div>

            <PaymentPlanEditorModal
                isOpen={planToEdit !== null}
                itineraryId={planToEdit?.itineraryId}
                highlightInstallmentId={planToEdit?.installmentId}
                formatAmount={formatPlanAmount}
                onClose={() => setPlanToEdit(null)}
            />
        </PrivateLayout>
    );
};

export default PaymentsPage;
